import { BookOpen } from "lucide-react";
import { z } from "zod";

import { SECTION_TYPES } from "@/lib/constants/sections";
import { constructImageUrl } from "@/lib/utils";

import type { WebsiteWithImages } from "@/types/prisma";

export const AboutSectionSchema = z.object({
  type: z.literal(SECTION_TYPES.ABOUT),
  content: z.object({
    header: z.string().describe("The header of the about section."),
    history: z.string().describe("The company history or background story."),
    image: z.string().describe("The image URL for the about section."),
    alignText: z.enum(["left", "right"]).describe("Text alignment relative to the image."),
    variantNumber: z.number().optional(),
    stats: z
      .array(z.object({ value: z.string(), label: z.string() }))
      .optional()
      .describe("Optional statistics to display."),
  }),
});

export type AboutSection = z.infer<typeof AboutSectionSchema>;

const aboutSection = {
  type: SECTION_TYPES.ABOUT,
  content: {
    header: "A little bit about us",
    history:
      "We've been in business for 60 years. We are a full-service company that provides everything from design to installation and maintenance. We are a team of professionals who are passionate about what we do. We take pride in our work and are committed to providing the best service possible. We are dedicated to creating beautiful outdoor spaces that our clients can enjoy for years to come. We are committed to sustainability and use environmentally friendly practices whenever possible. We are proud to be a part of the community and look forward to working with you.",
    image:
      "https://images.unsplash.com/photo-1588758167582-5c1021c30e0a?q=80&w=800&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    alignText: "left",
    stats: [
      { value: "10+", label: "Years in business" },
      { value: "800+", label: "Projects completed" },
      { value: "1000+", label: "Happy customers" },
    ],
  },
  meta: {
    displayName: "About Us",
    displayDescription: "Tell your customers about your company, how long you've been in business, etc.",
    displayIcon: BookOpen,
    variantsCount: 7,
  },
  aiPrompt: (website: WebsiteWithImages) => {
    const images = website.images.map((image) => constructImageUrl(image.providerId, image.source!));

    return `In the About section, tell a compelling story about the company or organization as the "history" field.
    Highlight the mission, values, and unique aspects that set it apart from competitors.
    YOU ARE TO STTRICTLY KEEP THE STORY SHORT AND STRICTLY DO NOT EXCEED 90 WORDS.
    Use this section to build an emotional connection with visitors and establish a strong brand identity.

     ${
       images
         ? // eslint-disable-next-line quotes
           `Additionally, pick an image suitable from the "images" field:` +
           images.join(" | ") +
           "\nYOU ARE TO ONLY USE IMAGE URLS FROM THE PROVIDED IMAGE URLS AND NOT MAKE UP OF ANY URL."
         : ""
     }`;
  },
};

export default aboutSection;
